import { Button, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { getUserInfo } from "@storyboard/web-client";
import { transport } from "@/config";

/** Cooldown before the retry button re-enables after a failed probe. */
const RETRY_COOLDOWN_MS = 5000;

export default function MaintenanceMode() {
  const theme = useTheme();
  const [coolingDown, setCoolingDown] = useState<boolean>(false);

  // Liveness probe: the API answering /userinfo again means maintenance is over.
  const probe = useQuery({
    queryKey: ["maintenance", "user-info-probe"],
    queryFn: () => getUserInfo(transport),
    refetchInterval: 60_000,
    // Keep probing from background tabs so recovery still redirects a parked tab out of maintenance.
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  useEffect(() => {
    if (probe.isSuccess) {
      window.location.href = "/";
    }
  }, [probe.isSuccess]);

  useEffect(() => {
    if (probe.errorUpdatedAt) {
      setCoolingDown(true);
      const timeout = setTimeout(() => setCoolingDown(false), RETRY_COOLDOWN_MS);
      return () => clearTimeout(timeout);
    }
  }, [probe.errorUpdatedAt]);

  const disabled = probe.isFetching || coolingDown;

  const background =
    // @ts-ignore
    theme?.components?.MuiCssBaseline?.styleOverrides?.body?.background ?? "#222";

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "fixed",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background,
      }}
    >
      <Box
        sx={{
          padding: 3,
          borderRadius: 2,
          background: theme.palette.secondary.dark,
          textAlign: "center",
        }}
      >
        <Box sx={{ fontSize: "1.5rem", mb: 1, fontWeight: "bold" }}>System Maintenance</Box>
        <Box>We are currently performing system maintenance.</Box>
        <Box sx={{ mb: 2 }}>Please check back in a few minutes.</Box>
        <Button variant="contained" disabled={disabled} onClick={() => probe.refetch()}>
          {disabled ? "Checking Status..." : "Try Again"}
        </Button>
      </Box>
    </Box>
  );
}
